import React from 'react';
import { ArrowRightIcon, ClockIcon, MapPinIcon, PhoneIcon } from 'lucide-react';
import { company } from '../data/site';
import { InquiryForm } from './InquiryForm';
type ContactSectionProps = {
  eyebrow?: string | null;
  title?: string;
  intro?: string;
  action?: {
    label: string;
    href: string;
  };
  secondaryAction?: {
    label: string;
    href: string;
  };
  location?: string;
  showForm?: boolean;
};
export function ContactSection({
  eyebrow = 'Let’s talk about your property',
  title = 'What would you like to create, improve or care for?',
  intro = 'Tell us about your residential landscape project, land development or property-care needs for your business or HOA community. We’ll help you find the right place to start.',
  action,
  secondaryAction,
  location = company.serviceArea,
  showForm = true
}: ContactSectionProps) {
  return <section id="contact" aria-labelledby="contact-title" className="section-y bg-olive text-ivory" data-id="element-73" data-mp="1suke6wkuufy0">
      <div className={`site-container grid gap-9 lg:grid-cols-12 lg:gap-x-16 ${showForm ? 'lg:gap-y-9' : 'lg:items-start'}`} data-id="element-74" data-mp="1wrymmm0pojoy">
        <div className={showForm ? 'lg:col-span-5' : 'lg:col-span-6'} data-id="element-75" data-mp="2nbjdzirm8s0">
          {eyebrow ? <p className="eyebrow mb-4 text-ivory/70" data-id="element-76" data-mp="2pel3gta5qck">{eyebrow}</p> : null}
          <h2 id="contact-title" className="font-display text-[36px] font-medium leading-[1.06] sm:text-[48px] lg:text-[52px]" data-id="element-77" data-mp="go5w2ucuivwh">
            {title}
          </h2>
          <p className="mt-6 max-w-[580px] text-[16px] leading-relaxed text-ivory/80 sm:text-[17px]" data-id="element-78" data-mp="qk8v3rceief7">{intro}</p>
          {action || secondaryAction ? <div className="mt-7 flex flex-col items-start gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4" data-id="element-79" data-mp="lk3c0zk7608q">
              {action ? <a href={action.href} className="focus-ring group inline-flex min-h-[52px] items-center justify-center gap-3 bg-ivory px-6 text-[15px] font-medium text-olive transition-colors duration-150 hover:bg-stone" data-id="element-80" data-mp="vbh970ooenyp">
                  {action.label}
                  <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-out group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-81" data-mp="202er93h7rqs" />
                </a> : null}
              {secondaryAction ? <a href={secondaryAction.href} className="focus-ring group inline-flex min-h-[52px] items-center justify-center gap-3 border border-ivory/50 px-6 text-[15px] font-medium text-ivory transition-colors duration-150 hover:border-ivory hover:bg-ivory/5" data-id="element-82" data-mp="axurq6lq6nb0">
                  {secondaryAction.label}
                  <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-out group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-83" data-mp="uozscywqt0d7" />
                </a> : null}
            </div> : null}
        </div>

        {showForm ? <div className="lg:col-span-7 lg:col-start-6 lg:row-span-2 lg:row-start-1" data-id="element-84" data-mp="30fv6hmh65uw9">
            <InquiryForm data-id="element-85" data-mp="2sxnjgtv4q548" />
          </div> : null}

        <div className={showForm ? 'lg:col-span-5 lg:row-start-2' : 'lg:col-span-5 lg:col-start-8 lg:row-start-1 lg:pt-2'} data-id="element-86" data-mp="2sz70imom0k72">
          <a href={company.phoneHref} className="focus-ring inline-flex min-h-[52px] items-center justify-center gap-2.5 border border-ivory/50 px-6 text-[15px] font-medium transition-colors hover:border-ivory hover:bg-ivory/5" data-id="element-87" data-mp="2j7arigkzj9o2">
            <PhoneIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-88" data-mp="3bw84q5acag9y" />
            Call {company.phoneDisplay}
          </a>
          <dl className="mt-7 grid gap-6 border-t border-ivory/15 pt-6 sm:grid-cols-2 lg:gap-7" data-id="element-89" data-mp="36xois4vqx4hl">
            <div className="flex gap-3" data-id="element-90" data-mp="32tgu27pafojd">
              <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0 text-ivory/60" strokeWidth={1.5} aria-hidden="true" data-id="element-91" data-mp="2xwgwkl6i7u3v" />
              <div data-id="element-92" data-mp="1rgeo06ktehdg">
                <dt className="eyebrow text-ivory/60" data-id="element-93" data-mp="1rgwfyr0g29fk">Office</dt>
                <dd className="mt-2 text-[15px] leading-relaxed" data-id="element-94" data-mp="1mgik07c4apxw">{company.addressLine1}<br data-id="element-95" data-mp="1mjehxvnytemc" />{company.addressLine2}</dd>
              </div>
            </div>
            <div className="flex gap-3" data-id="element-96" data-mp="3acf0f9r44y1w">
              <ClockIcon className="mt-0.5 h-4 w-4 shrink-0 text-ivory/60" strokeWidth={1.5} aria-hidden="true" data-id="element-97" data-mp="3f4zr42eq2v17" />
              <div data-id="element-98" data-mp="31dunyqc5bkm4">
                <dt className="eyebrow text-ivory/60" data-id="element-99" data-mp="31ecpa1do0p0w">Office hours</dt>
                <dd className="mt-2 text-[15px] leading-relaxed" data-id="element-100" data-mp="35e50klug199y">{company.hours}</dd>
              </div>
            </div>
          </dl>
          <p className="mt-6 max-w-[580px] text-[14.5px] leading-relaxed text-ivory/70" data-id="element-101" data-mp="3t4qyvlenbf6r">{location}</p>
        </div>
      </div>
    </section>;
}